import React from 'react';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { FaArrowRight, FaCheck } from 'react-icons/fa';
import Header from './Header';
import Footer from './Footer';
import { useLang } from '../../i18n/LanguageContext';

// ── Porte d'entrée marchand Shopify ───────────────────────────────
// Page publique dédiée : elle montre le tunnel produit → plateforme → publier,
// et surtout elle pose `?source=shopify` sur l'inscription. C'est ce paramètre
// qui est figé en base (users.signup_source) à la création du compte, et qui
// déclenche ensuite le parcours marchand — sans lui, un nouveau marchand
// recevait le parcours générique « annonceur ».
const SIGNUP_URL = '/signup?source=shopify';

// Accents éclaircis : sur cartes sombres, les teintes d'origine
// (#5E8E3E, #1877f2…) descendent sous le seuil de lisibilité.
const STEPS = [
  { n: '01', titleKey: 'landing.merchant.step1', descKey: 'landing.merchant.step1Desc', color: '#a3e635' },
  { n: '02', titleKey: 'landing.merchant.step2', descKey: 'landing.merchant.step2Desc', color: '#7dd3fc' },
  { n: '03', titleKey: 'landing.merchant.step3', descKey: 'landing.merchant.step3Desc', color: '#c4b5fd' },
  { n: '04', titleKey: 'landing.merchant.step4', descKey: 'landing.merchant.step4Desc', color: '#fbbf24' },
];

const PROOF_KEYS = ['landing.merchant.proof1', 'landing.merchant.proof2', 'landing.merchant.proof3'];

const ShopifyLanding = () => {
  const { t } = useLang();

  return (
    <>
      <Header />

      <style>{`
        .sl-cta { transition: all .2s ease; }
        .sl-cta:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(5,150,105,.35) !important; }
        .sl-ghost { transition: all .2s ease; }
        .sl-ghost:hover { background: rgba(255,255,255,.12) !important; }
        .sl-step { transition: transform .2s ease, box-shadow .2s ease; }
        .sl-step:hover { transform: translateY(-4px); border-color: rgba(255,255,255,.18) !important; box-shadow: 0 34px 74px -22px rgba(0,0,0,.8) !important; }
        @media (max-width: 640px) { .sl-br { display: none; } }
      `}</style>

      {/* ── Hero marchand ── */}
      <section style={{
        background: 'linear-gradient(160deg, #0A0821 0%, #17123B 55%, #0F2A22 100%)',
        padding: '128px 24px 80px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '-15%', right: '-8%', width: 520, height: 520, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(149,191,71,0.18) 0%, transparent 65%)',
          filter: 'blur(70px)', pointerEvents: 'none',
        }} />

        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 16px', borderRadius: 999,
            border: '1px solid rgba(149,191,71,0.45)', background: 'rgba(149,191,71,0.12)',
            color: '#B9D98A', fontSize: 12, fontWeight: 700, letterSpacing: '.08em',
            textTransform: 'uppercase', marginBottom: 24,
          }}>🛍️ {t('landing.merchant.badge')}</span>

          <h1 style={{
            fontSize: 'clamp(32px,5.5vw,56px)', fontWeight: 900, lineHeight: 1.1,
            color: '#fff', letterSpacing: '-.03em', marginBottom: 20, maxWidth: 18 + 'ch',
            textWrap: 'balance',
          }}>
            {t('landing.merchant.title1')}<br className="sl-br" />{' '}
            <span style={{
              background: 'linear-gradient(135deg,#95BF47,#5FCFA5)', WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>{t('landing.merchant.title2')}</span>
          </h1>

          <p style={{
            fontSize: 17, color: 'rgba(255,255,255,.78)', lineHeight: 1.7,
            maxWidth: '58ch', marginBottom: 32,
          }}>{t('landing.merchant.sub')}</p>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 40 }}>
            <Link to={SIGNUP_URL} className="sl-cta" style={{
              display: 'inline-flex', alignItems: 'center', gap: 9, padding: '15px 30px', borderRadius: 12,
              background: 'linear-gradient(135deg,#5E8E3E,#047857)', color: '#fff',
              fontWeight: 700, fontSize: 15.5, textDecoration: 'none',
              boxShadow: '0 4px 20px rgba(5,150,105,.4)',
            }}>
              🛍️ {t('landing.merchant.ctaConnect')} <FaArrowRight style={{ fontSize: 12 }} />
            </Link>
            <HashLink smooth to="/shopify#etapes" className="sl-ghost" style={{
              display: 'inline-flex', alignItems: 'center', padding: '15px 28px', borderRadius: 12,
              background: 'rgba(255,255,255,.08)', border: '1.5px solid rgba(255,255,255,.25)',
              color: '#fff', fontWeight: 600, fontSize: 15, textDecoration: 'none',
            }}>{t('landing.merchant.ctaSecondary')}</HashLink>
          </div>

          {/* Preuves — ce que ça change concrètement */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {PROOF_KEYS.map((k) => (
              <div key={k} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14.5, color: 'rgba(255,255,255,.72)' }}>
                <span style={{
                  width: 18, height: 18, borderRadius: '50%', background: 'rgba(149,191,71,.18)',
                  border: '1px solid rgba(149,191,71,.5)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', flexShrink: 0, marginTop: 2,
                }}><FaCheck style={{ fontSize: 8, color: '#B9D98A' }} /></span>
                {t(k)}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Les 4 étapes ── */}
      <section id="etapes" style={{ background: 'radial-gradient(120% 80% at 50% 0%, #120E33 0%, #08061E 62%)', padding: '88px 24px', position: 'relative', overflow: 'hidden' }}>
      <div className="oba-aurora" style={{ top: '-10%', right: '-10%', width: 520, height: 520, background: 'radial-gradient(circle, rgba(94,142,62,.2) 0%, transparent 68%)' }} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <h2 style={{
            fontSize: 'clamp(26px,4vw,38px)', fontWeight: 800, color: '#ffffff',
            letterSpacing: '-.02em', marginBottom: 12, textAlign: 'center', textWrap: 'balance',
          }}>{t('landing.about.howTitle1')}{' '}
            <span style={{
              background: 'linear-gradient(120deg,#a3e635,#5eead4)', WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>{t('landing.about.howTitle2')}</span>
          </h2>
          <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.62)', fontSize: 15.5, maxWidth: '52ch', margin: '0 auto 52px', lineHeight: 1.65 }}>
            {t('landing.merchant.proofTitle')}
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px,1fr))', gap: 20 }}>
            {STEPS.map((s) => (
              <div key={s.n} className="sl-step" style={{
                padding: '28px 24px', borderRadius: 16,
                background: 'linear-gradient(160deg, rgba(24,19,60,.72), rgba(11,8,32,.88))',
                border: '1px solid rgba(255,255,255,0.09)', boxShadow: '0 24px 60px -22px rgba(0,0,0,.7)',
              }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: s.color, letterSpacing: '.05em', marginBottom: 14 }}>{s.n}</div>
                <div style={{ width: 32, height: 3, background: s.color, borderRadius: 2, marginBottom: 18, opacity: .5 }} />
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 10, lineHeight: 1.35 }}>{t(s.titleKey)}</h3>
                <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.62)', lineHeight: 1.65, margin: 0 }}>{t(s.descKey)}</p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <Link to={SIGNUP_URL} className="sl-cta" style={{
              display: 'inline-flex', alignItems: 'center', gap: 9, padding: '15px 34px', borderRadius: 12,
              background: 'linear-gradient(135deg,#5E8E3E,#047857)', color: '#fff',
              fontWeight: 700, fontSize: 15.5, textDecoration: 'none',
              boxShadow: '0 4px 20px rgba(5,150,105,.35)',
            }}>
              🛍️ {t('landing.merchant.ctaConnect')} <FaArrowRight style={{ fontSize: 12 }} />
            </Link>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.42)', marginTop: 14 }}>{t('landing.pricing.note')}</p>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ShopifyLanding;
